import { Card } from '@chakra-ui/react';
import { formatCurrency, formatCurrencyDelta } from '@monetize/utils/core';
import { FunctionComponent as FC } from 'react';
import { MCard, MDivider, MGrid, MText, TruncatedText } from '../../Monetize';

interface QuoteAnalyticsCardProps {
  label: string;
  amount: number;
  incrementalAmount?: number;
  currency: string;
  offerings?: {
    name: string;
    amount: number;
    incrementalAmount?: number;
    products: {
      name: string;
      amount: number;
      incrementalAmount?: number;
    }[];
  }[];
}

// Value columns grow to fit the widest amount so every row stays aligned
const VALUE_COLUMN = 'minmax(110px, max-content)';
const GRID_COLUMNS = `minmax(0, 1fr) ${VALUE_COLUMN}`;
const GRID_COLUMNS_WITH_INCREMENTAL = `${GRID_COLUMNS} ${VALUE_COLUMN}`;

// Each row spans the card grid and reuses its columns
const ROW_PROPS = { gridColumn: '1 / -1', templateColumns: 'subgrid' };

const ValueColumns: FC<{
  amount: number;
  incrementalAmount?: number;
  currency: string;
  fontSize?: string;
  fontWeight?: number;
}> = ({ amount, incrementalAmount, currency, fontSize, fontWeight }) => (
  <>
    <MText
      textAlign="right"
      whiteSpace="nowrap"
      fontSize={fontSize}
      fontWeight={fontWeight}
    >
      {formatCurrency(amount, { currency })}
    </MText>
    {incrementalAmount !== undefined && (
      <MText
        textAlign="right"
        whiteSpace="nowrap"
        fontSize={fontSize}
        fontWeight={fontWeight}
      >
        {formatCurrencyDelta(incrementalAmount, { currency })}
      </MText>
    )}
  </>
);

export const QuoteAnalyticsCard: FC<QuoteAnalyticsCardProps> = (
  props: QuoteAnalyticsCardProps,
) => {
  const { amount, incrementalAmount, label, offerings, currency } = props;
  const showIncremental = incrementalAmount !== undefined;

  return (
    <MCard variant="borderless" mt="2">
      <Card.Body>
        <MGrid
          templateColumns={
            showIncremental ? GRID_COLUMNS_WITH_INCREMENTAL : GRID_COLUMNS
          }
          columnGap={4}
        >
          {showIncremental && (
            <MGrid {...ROW_PROPS}>
              <MText
                gridColumn={2}
                textAlign="right"
                fontSize="xs"
                fontWeight={600}
                color="tGray.darkPurple"
              >
                TOTAL
              </MText>
              <MText
                textAlign="right"
                fontSize="xs"
                fontWeight={600}
                color="tGray.darkPurple"
              >
                INCREMENTAL
              </MText>
            </MGrid>
          )}
          <MGrid {...ROW_PROPS}>
            <MText fontSize="xl" overflowWrap="anywhere">
              {label}
            </MText>
            <ValueColumns
              amount={amount}
              incrementalAmount={incrementalAmount}
              currency={currency}
              fontSize="xl"
            />
          </MGrid>
          <MDivider gridColumn="1 / -1" />
          {offerings?.map((offering, i) => (
            <MGrid key={`${offering.name}-${i}`} {...ROW_PROPS} mt={6}>
              <MGrid {...ROW_PROPS}>
                <TruncatedText fontSize="md" fontWeight={600}>
                  {offering.name}
                </TruncatedText>
                <ValueColumns
                  amount={offering.amount}
                  incrementalAmount={offering.incrementalAmount}
                  currency={currency}
                  fontSize="md"
                  fontWeight={600}
                />
              </MGrid>
              <MDivider gridColumn="1 / -1" />
              {offering.products.map((product) => (
                <MGrid key={product.name} {...ROW_PROPS} mt={2}>
                  <TruncatedText pl={4}>{product.name}</TruncatedText>
                  <ValueColumns
                    amount={product.amount}
                    incrementalAmount={product.incrementalAmount}
                    currency={currency}
                  />
                </MGrid>
              ))}
            </MGrid>
          ))}
        </MGrid>
      </Card.Body>
    </MCard>
  );
};
